import { useState } from "react";
import { VStack, HStack, Button, Icon } from "@/shared/ui/primitives";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

type FAQCategory = "overview" | "manual" | "operation";

interface FAQItem {
	question: string;
	answer: string;
}

export const FAQSection = () => {
	const { t } = useTranslation();
	const [activeCategory, setActiveCategory] = useState<FAQCategory>("overview");
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	const faqData: Record<FAQCategory, FAQItem[]> = {
		overview: t("faq.overview", { returnObjects: true }) as FAQItem[],
		manual: t("faq.manual", { returnObjects: true }) as FAQItem[],
		operation: t("faq.operation", { returnObjects: true }) as FAQItem[],
	};

	return (
		<VStack className="w-full gap-[28px]">
			<h2 className="text-[1.5rem] md:text-[2.25rem] text-ct-strong font-semibold text-center">{t("home.faq.title")}</h2>

			{/* 카테고리 탭 */}
			<HStack className="gap-[16px] justify-center flex-wrap">
				{(["overview", "manual", "operation"] as FAQCategory[]).map((category) => (
					<Button
						key={category}
						variant={activeCategory === category ? "primary" : "default"}
						onClick={() => {
							setActiveCategory(category);
							setOpenIndex(null);
						}}
					>
						{t(`home.faq.${category}Title`)}
					</Button>
				))}
			</HStack>

			{/* FAQ 아이템 */}
			<VStack className="w-full gap-[16px]">
				{faqData[activeCategory].map((item, index) => (
					<motion.div
						key={index}
						className="w-full"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: index * 0.1 }}
					>
						<VStack className="w-full rounded-[16px] bg-bg-regular overflow-hidden">
							<button
								className="w-full p-[24px] text-left flex items-start justify-start"
								onClick={() => setOpenIndex(openIndex === index ? null : index)}
							>
								<HStack className="justify-between gap-[16px]">
									<span className="text-lg text-ct-strong font-semibold flex-1">{item.question}</span>
									<motion.div
										animate={{ rotate: openIndex === index ? 180 : 0 }}
										transition={{ duration: 0.3 }}
									>
										<Icon name="Down" width={24} height={24} className="text-ct-regular" />
									</motion.div>
								</HStack>
							</button>

							<AnimatePresence>
								{openIndex === index && (
									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: "auto", opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
										transition={{ duration: 0.3 }}
									>
										<div className="px-[24px] pb-[24px]">
											<p className="text-md text-ct-regular font-medium whitespace-pre-line">
												{item.answer}
											</p>
										</div>
									</motion.div>
								)}
							</AnimatePresence>
						</VStack>
					</motion.div>
				))}
			</VStack>
		</VStack>
	);
};
